import { nanoid } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import type { RequestedConfig } from '@/widgets/chartsView';
import { ChartType, getDataRequestedConfig } from '@/widgets/chartsView';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { DimensionColumnDataType } from '@/entities/columnsConfig';
import { prepareDateRangeValueObject } from '@/shared/api';
import { parseMediaColumnValue } from '@/shared/data';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import { DateRangePresets, DateScale, Lifetime } from '@/shared/types';
import * as CohortChartAPI from '../api/cohortChartAPI';
import type { CohortChartState, CohortDataResponse, ChartMeta } from './types';

type CohortChartRequestedConfig = RequestedConfig<CohortChartState>;

interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CohortChartState | null;
  chartData:
    | {
        status: DataStatus.idle | DataStatus.error;
        data: null;
      }
    | {
        status: DataStatus.finished;
        requestedConfig: CohortChartRequestedConfig;
        data: CohortDataResponse;
      }
    | {
        status: DataStatus.loading;
        requestedConfig: CohortChartRequestedConfig;
        data: null;
      };
}

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  chart: null,
  chartData: {
    status: DataStatus.idle,
    data: null,
  },
};

export const cohortChartSlice = createAppSlice({
  name: 'cohortChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CohortChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (
              isLoading(state.cohortChart.meta.status) ||
              isFinished(state.cohortChart.meta.status)
            ) {
              return false;
            }
          },
        },
      },
    ),
    updateDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.dateScale = action.payload;
    }),
    updateDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.dateRange = action.payload;
      },
    ),
    updateLifetime: create.reducer<number>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.lifetime = action.payload;
    }),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.splitDimension = action.payload;
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.primaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric].type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.secondaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric].type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    updateChart: create.reducer<CohortChartState>((state, action) => {
      state.chart = action.payload;
    }),
    setupVendorDefault: create.reducer<{ lifetimeTwoYearsExtension: boolean }>(
      (state, action) => {
        if (!isMetaFinished(state.meta)) {
          state.chart = null;

          return;
        }

        state.chart = {
          id: nanoid(),
          type: ChartType.cohort,
          excludedSplitOptions: [],
          syncedYAxes: true,
          filters: [],
          dateScale: DateScale.DAY,
          dateRange: { preset: DateRangePresets.LAST_90_DAYS },
          lifetime: action.payload.lifetimeTwoYearsExtension
            ? Lifetime.TWO_YEARS
            : Lifetime.YEAR,
          probabilisticAttribution: true,
          splitDimension: state.meta.data.defaultAxis.dimension,
          primaryMetric: state.meta.data.defaultAxis.primaryMetric,
          secondaryMetric: state.meta.data.defaultAxis.secondaryMetric,
        };
      },
    ),
    resetChartData: create.reducer((state) => {
      state.chartData = {
        status: DataStatus.idle,
        data: null,
      };
    }),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
    getChartData: create.asyncThunk(
      (chart: CohortChartState, { getState }): Promise<CohortDataResponse> => {
        const state = getState() as RootState;

        if (!isFinished(state.cohortChart.meta.status)) {
          return Promise.reject(new Error('Meta not initialized'));
        }

        const { meta } = state.cohortChart;
        const { dimensionsFilters, metricsFilters } = prepareFilters(
          chart.filters,
          meta.data!.metrics,
          meta.data!.dimensions,
        );

        return CohortChartAPI.getCohortData({
          split: chart.splitDimension,
          metrics: [chart.primaryMetric, chart.secondaryMetric].filter(Boolean),
          dateScale: chart.dateScale,
          dateRange: prepareDateRangeValueObject(chart.dateRange),
          lifetime: chart.lifetime,
          probabilisticAttribution: chart.probabilisticAttribution,
          dimensionsFilters,
          metricsFilters,
        });
      },
      {
        pending: (state, action) => {
          state.chartData = {
            status: DataStatus.loading,
            requestedConfig: getDataRequestedConfig(
              action.meta.arg,
            ) as CohortChartRequestedConfig,
            data: null,
          };
        },
        fulfilled: (state, action) => {
          if (
            (isRequestedConfigExist(state.chartData) &&
              !isEqual(
                state.chartData.requestedConfig,
                getDataRequestedConfig(action.meta.arg),
              )) ||
            !state.chart ||
            !isMetaFinished(state.meta)
          ) {
            return;
          }

          const previousSplitDimension = isRequestedConfigExist(state.chartData)
            ? state.chartData.requestedConfig.splitDimension
            : null;

          state.chartData = {
            requestedConfig: isRequestedConfigExist(state.chartData)
              ? state.chartData.requestedConfig
              : (getDataRequestedConfig(
                  action.meta.arg,
                ) as CohortChartRequestedConfig),
            status: DataStatus.finished,
            data: action.payload,
          };
          const { splitDimension } = action.meta.arg;
          const optionsToExclude = (
            state.chartData.data.splitOptions[splitDimension] || []
          ).slice(3);
          const isMediaContent = selectSplitDimensionIsMedia({
            cohortChart: state,
          });

          if (previousSplitDimension !== splitDimension) {
            state.chart.excludedSplitOptions = isMediaContent
              ? optionsToExclude.map((option) => {
                  return parseMediaColumnValue(option).value;
                })
              : optionsToExclude;
          }
        },
        rejected: (state, action) => {
          if (
            isRequestedConfigExist(state.chartData) &&
            !isEqual(
              state.chartData.requestedConfig,
              getDataRequestedConfig(action.meta.arg),
            )
          ) {
            return;
          }

          state.chartData.status = DataStatus.error;
        },
      },
    ),
    toggleExcludedSplitOption: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      const optionIndex =
        state.chart?.excludedSplitOptions.indexOf(action.payload) ?? -1;

      if (optionIndex !== -1) {
        state.chart.excludedSplitOptions =
          state.chart.excludedSplitOptions.toSpliced(optionIndex, 1);
      } else {
        state.chart.excludedSplitOptions.push(action.payload);
      }
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
    selectChartData: (state) => state.chartData,
    selectSplitDimensionIsMedia: (state) => {
      if (!state.meta.data || !state.chart) {
        return false;
      }

      return (
        state.meta.data.config[state.chart.splitDimension]?.type ===
        DimensionColumnDataType.MEDIA
      );
    },
    selectDimensionIsDate: (state) => {
      if (!state.meta.data || !state.chart) {
        return false;
      }

      return (
        state.meta.data.config[state.chart.splitDimension]?.type ===
        DimensionColumnDataType.DATE
      );
    },
  },
});

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

export function isRequestedConfigExist(
  chartsData: State['chartData'],
): chartsData is
  | {
      status: DataStatus.loading;
      requestedConfig: CohortChartRequestedConfig;
      data: null;
    }
  | {
      status: DataStatus.finished;
      requestedConfig: CohortChartRequestedConfig;
      data: CohortDataResponse;
    } {
  return (
    chartsData.status === DataStatus.loading ||
    chartsData.status === DataStatus.finished
  );
}

export const {
  initMeta,
  updateDateScale,
  updateDateRange,
  updateLifetime,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateFilters,
  getChartData,
  updateChart,
  setupVendorDefault,
  resetChartData,
  reset,
  toggleExcludedSplitOption,
} = cohortChartSlice.actions;
export const {
  selectChart,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectChartData,
  selectSplitDimensionIsMedia,
  selectDimensionIsDate,
} = cohortChartSlice.selectors;
